// Zustellung einer Zeitbuchung mit Offline-Garantie (Audit B-1/B-2). Prinzip: DURABEL ZUERST.
// Das Item wird IMMER synchron in die Outbox gelegt, bevor irgendetwas Asynchrones passiert — ab da
// überlebt die gestoppte Zeit Crash/Kill/Reload/Standby (schließt das RAM-Fenster zwischen clearTimer()
// und Server-ACK). Der client_uuid wird EINMAL erzeugt und für Direkt-Versuch UND Replay genutzt, daher
// dedupliziert der Server jeden Wiederholungs-POST (UNIQUE(order_id, client_uuid)) → keine Doppelbuchung.

import { ApiError, api } from '../api/client'
import { deadLetter, enqueue, flushOutbox, loadOutbox, removeFromOutbox } from '../offline/outbox'
import { serverAdjustedNow } from '../offline/serverClock'
import { newUuid } from '../offline/uuid'

export type SubmitResult = 'sent' | 'queued'

export async function submitTime(orderId: number, seconds: number, startedAtMs?: number): Promise<SubmitResult> {
  const clientUuid = newUuid()
  // A3 Timer-Gate: Startzeitpunkt serverkorrigiert mitgeben (gleiche Eichung wie stamped_at) — der lokal
  // erfasste startedAt wird um den aktuellen Uhren-Offset verschoben, damit eine falsch gehende Tablet-Uhr
  // die serverseitige Anwesenheits-Deckungsprüfung nicht mit Fehlalarmen flutet.
  const body: { seconds: number; client_uuid: string; started_at?: number } = { seconds, client_uuid: clientUuid }
  if (startedAtMs != null) body.started_at = startedAtMs + (serverAdjustedNow() - Date.now())
  const item = { id: clientUuid, method: 'POST' as const, path: `orders/${orderId}/time`, body }

  // 1. Durabel zuerst: ab hier ist die Zeit persistiert, egal was als Nächstes passiert.
  enqueue(item)

  // FIFO wahren (A3-Review): warten ÄLTERE Items in der Outbox (z. B. ein transient gescheiterter
  // kommen-Stempel), darf die Buchung sie nicht online überholen — der Server prüfte die Deckung sonst
  // gegen noch-nicht-zugestellte Stempel (falscher Chef-Alarm). Der Flush stellt alles in Reihenfolge zu
  // (Re-Entranz sichert flushOutbox selbst); die Buchung reist dabei mit.
  const queue = loadOutbox()
  if (queue.length > 0 && queue[0]!.id !== item.id) {
    void flushOutbox()
    return 'queued'
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'queued'

  try {
    await api.addTime(orderId, body)
    removeFromOutbox(item.id) // 2a. bestätigt → aus der Live-Queue
    return 'sent'
  } catch (err) {
    if (err instanceof ApiError) {
      // 401: Session weg → in der Queue lassen (Retry nach Re-Login) und zentrales 401-Handling auslösen (#1).
      if (err.status === 401) throw err
      // Terminaler 4xx (z. B. 404, Auftrag gelöscht): nicht wiederholbar, aber NICHT still verlieren —
      // in den Dead-Letter-Bucket verschieben und den Fehler sichtbar nach oben reichen.
      if (err.status < 500) {
        deadLetter(item.id)
        throw err
      }
    }
    // 2b. Netzfehler oder transienter 5xx → in der Queue lassen, geht automatisch beim nächsten Flush raus.
    return 'queued'
  }
}
